import { CalculationType } from '../../../../../../@types/enums';
import { siteCalculationState } from '../../../../../../services/siteCalculationState';
import { useViewerStore } from '../../../../../../store/store';
import { SetModuleGraph, SetShadeMeshOnModule } from '../../../Three-JS-Viewer/Controllers/ModuleControl';
import { SetShadeFinalResult } from '../../../Three-JS-Viewer/Controllers/RefreshShade';
import { beginFinalizeModuleGraph, endFinalizeModuleGraph } from '../../../Three-JS-Viewer/Controllers/TreeImpactEligibility';

let batchLayoutSiteCalcDepth = 0;

/** Suppress MODULE_GRAPH shade store writes while generating many layout variants in a row. */
export function beginBatchLayoutSiteCalc(): void {
    if (batchLayoutSiteCalcDepth === 0) {
        useViewerStore.setState({ aiBatchLayoutMutating: true } as any);
        siteCalculationState.clearNeedsUpdate(CalculationType.MODULE_GRAPH);
    }
    batchLayoutSiteCalcDepth += 1;
}

async function runAuthoritativeModuleGraph(sceneViewer: unknown): Promise<void> {
    if (!sceneViewer) return;
    const self = sceneViewer as { roofMeshArr?: unknown[] };
    beginFinalizeModuleGraph();
    try {
        if (Array.isArray(self.roofMeshArr) && self.roofMeshArr.length) {
            SetShadeMeshOnModule(self.roofMeshArr);
        }
        await SetModuleGraph(sceneViewer as any);
        SetShadeFinalResult(sceneViewer as any);
    } finally {
        endFinalizeModuleGraph();
    }
}

async function flushBatchLayoutSiteCalc(sceneViewer: unknown): Promise<void> {
    siteCalculationState.clearNeedsUpdate(CalculationType.MODULE_GRAPH);
    useViewerStore.setState({ aiBatchLayoutMutating: false } as any);
    await runAuthoritativeModuleGraph(sceneViewer);
}

/** Flush a single shade summary update after the outermost batch of layout mutations completes. */
export async function endBatchLayoutSiteCalc(sceneViewer: unknown): Promise<void> {
    if (batchLayoutSiteCalcDepth <= 0) return;
    batchLayoutSiteCalcDepth -= 1;
    if (batchLayoutSiteCalcDepth > 0) return;
    await flushBatchLayoutSiteCalc(sceneViewer);
}

/** Always end batch (e.g. pipeline `finally`) even if begin/end pairs were unbalanced. */
export async function forceEndBatchLayoutSiteCalc(sceneViewer: unknown): Promise<void> {
    batchLayoutSiteCalcDepth = 0;
    await flushBatchLayoutSiteCalc(sceneViewer);
}

/**
 * Run one authoritative shade + module-graph pass mid-batch (per layout variant).
 * Batch mode suppresses shade writes; without this flush, removeGrey and metrics read zeros.
 */
export async function flushVariantLayoutSiteCalc(sceneViewer: unknown): Promise<void> {
    siteCalculationState.clearNeedsUpdate(CalculationType.MODULE_GRAPH);
    useViewerStore.setState({ aiBatchLayoutMutating: false } as any);
    await runAuthoritativeModuleGraph(sceneViewer);
    useViewerStore.setState({ aiBatchLayoutMutating: true } as any);
}

/** True while `beginBatchLayoutSiteCalc` is active (nested begins increment depth). */
export function isBatchLayoutSiteCalcActive(): boolean {
    return batchLayoutSiteCalcDepth > 0;
}

/**
 * Wait for a complete `SetModuleGraph` before reading production / multiplier / efficiency.
 * Mid-batch callers must flush; otherwise shade writes are suppressed and cards store zeros.
 */
export async function awaitAuthoritativeModuleGraph(sceneViewer: unknown): Promise<void> {
    if (!sceneViewer) return;
    if (batchLayoutSiteCalcDepth > 0) {
        await flushVariantLayoutSiteCalc(sceneViewer);
        return;
    }
    await runAuthoritativeModuleGraph(sceneViewer);
}
